import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {pluralize} from 'BenchlingUtil';
import ChildImportPlanSection from 'config-migration/import/import-plan-sections/ChildImportPlanSection';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {HighImpactChange, MigrationError} from 'config-migration/MigrationConstants';
import {InlineErrorIcon, InlineWarningIcon} from 'config-migration/shared-ui-components/InlineIcon';
import {CollapsibleTriangle} from 'config-migration/shared-ui-components/TriangleToggle';
import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {MigrationTab} from 'config-migration/types/MigrationTab';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {GRAY_DARK, ORANGE_LIGHTEST, RED_POWDER} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Tag, {TagColor, TagSize} from 'ui-library/Tag';

import {ObjectAction, ObjectActionType} from '../../types/objectActions';
import {countChildHighImpactChanges} from '../HighImpactChanges';
import ObjectActionDropdown from '../ObjectActionDropdown';
import {computeFilteredActions} from '../planning-logic/ConfigMigrationPlan';

export interface ImportPlanSectionRowProps {
  plan: PrimaryObjectPlan;
  errors: Array<MigrationError>;
  nestedDepth: number;
  /**
   * Controls whether the dropdown is shown and modifiable. If true, the default action is just shown as text.
   */
  showActionInline?: boolean;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export function getImportPlanSectionRowHighImpactTestIdKey(objectId: string): string {
  return objectId;
}

export default function ImportPlanSectionRow(props: ImportPlanSectionRowProps): JSX.Element {
  const {plan, errors} = props;
  const hasErrors = errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const hasChildren = Boolean(plan.childMigrationObjectsByType.size);
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
  const highImpactChangeCount = countChildHighImpactChanges(plan, props.highImpactChangeMap);

  const {showIgnore, filteredAdditionalActions, showLockIcon} = computeFilteredActions(plan);

  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={plan.objectId}
        nestedDepth={props.nestedDepth}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          plan={plan}
          errors={errors}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          hasChildren={hasChildren}
          highImpactChangeCount={highImpactChangeCount}
          showLockIcon={showLockIcon}
        />
        <ObjectActionDropdownColumn
          plan={plan}
          onUpdatePlan={props.onUpdatePlan}
          showActionInline={props.showActionInline}
          showIgnore={showIgnore}
          filteredAdditionalActions={filteredAdditionalActions}
          disableDropdown={showLockIcon}
        />
      </ImportPlanSectionRowWrapper>
      {!isCollapsed &&
        hasChildren &&
        Array.from(plan.childMigrationObjectsByType.keys()).map((childType) => (
          <ChildImportPlanSection
            plan={props.plan}
            childType={childType}
            onUpdatePlan={props.onUpdatePlan}
            nestedDepth={props.nestedDepth}
            highImpactChangeMap={props.highImpactChangeMap}
            key={childType}
          />
        ))}
    </>
  );
}

export interface ImportPlanSectionRowWrapperProps {
  plan: PrimaryObjectPlan;
  hasErrors: boolean;
  hasWarnings: boolean;
  children: ReactNode;
  nestedDepth: number;
}

export function ImportPlanSectionRowWrapper(props: ImportPlanSectionRowWrapperProps): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-row',
        props.nestedDepth !== 0 && css(styles.childRow),
        props.plan.selectedAction.type === ObjectActionType.IGNORE && css(styles.ignoreRow),
        props.hasErrors && css(styles.errorRow),
        props.hasWarnings && !props.hasErrors && css(styles.warningRow)
      )}
      {...getTestProps({component: 'ImportPlanSectionRow', element: 'tableRow', key: props.plan.objectId})}
      data-amp-safe={false}
    >
      {props.children}
    </div>
  );
}

export function ObjectDisplayNameColumn(props: {
  hasErrors: boolean;
  hasWarnings: boolean;
  rowIcon?: BenchlingIconName;
  subLabel?: string;
  plan: PrimaryObjectPlan;
  errors: Array<MigrationError>;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isCollapsed: boolean;
  hasChildren: boolean;
  highImpactChangeCount: number;
  showLockIcon?: boolean;
}): JSX.Element {
  const renderErrorOrWarningIcon = (): JSX.Element | null => {
    if (props.hasErrors) {
      return (
        <InlineErrorIcon
          objectName={props.plan.objectDisplayName}
          migrationTab={MigrationTab.IMPORT}
          errors={props.errors}
        />
      );
    }
    if (props.hasWarnings) {
      return (
        <InlineWarningIcon
          objectName={props.plan.objectDisplayName}
          migrationTab={MigrationTab.IMPORT}
          errors={props.errors}
        />
      );
    }
    return null;
  };

  const getToggleClassName = (): string => {
    if (props.hasChildren && props.rowIcon) {
      return css(styles.parentToggle, styles.toggleWithIcon);
    }
    if (props.hasChildren) {
      return css(styles.parentToggle);
    }
    return css(styles.noToggle);
  };

  return (
    <>
      {/* Dedicated cell for lock icon, always present for alignment */}
      <div
        className="simpleDivTable-cell"
        style={{
          width: 28,
          minWidth: 28,
          maxWidth: 28,
          paddingRight: 0,
          paddingLeft: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {props.showLockIcon && (
          <div className={css(styles.lockIconBlockWrapper)}>
            <Popover
              renderContent={() => (
                <div style={{maxWidth: 320}}>
                  <div>
                    You don't have permission to edit this item
                    <br />(
                    <span>
                      {props.plan.objectType
                        ?.toString()
                        .replace(/([A-Z])/g, ' $1')
                        .trim()
                        .toLowerCase() || 'item'}
                    </span>
                    , API ID <span>{props.plan.objectId}</span>).
                  </div>
                  <div style={{marginTop: 16}}>Contact Benchling support.</div>
                </div>
              )}
            >
              <BenchlingIcon icon="lock" />
            </Popover>
          </div>
        )}
      </div>
      {/* Main cell for row content */}
      <div
        className={classNames(
          'simpleDivTable-cell',
          props.subLabel ? css(styles.subLabelHeight) : null,
          props.hasErrors || props.hasWarnings ? css(styles.iconCell) : css(styles.firstCell),
          'u-flex-display u-vertical-center-items'
        )}
      >
        <div>
          {renderErrorOrWarningIcon()}
          {props.rowIcon ? <BenchlingIcon className="result-icon" icon={props.rowIcon} /> : null}
          <span
            onClick={() => {
              props.setIsCollapsed(!props.isCollapsed);
            }}
            className={getToggleClassName()}
            {...(props.hasChildren &&
              getTestProps({
                component: 'ImportPlanSectionRow',
                element: 'collapseToggle',
                key: props.plan.objectId,
              }))}
          >
            {props.hasChildren ? <CollapsibleTriangle isCollapsed={props.isCollapsed} /> : null}
            {props.plan.objectDisplayName}
            {props.highImpactChangeCount > 0 && (
              <span
                className={css(styles.highImpactParentIcon)}
                {...getTestProps({
                  component: 'ImportPlanSectionRow',
                  element: 'highImpactChangeCount',
                  key: getImportPlanSectionRowHighImpactTestIdKey(props.plan.objectId),
                })}
                data-amp-safe={false}
              >
                <Popover renderContent={() => getParentIconHoverText(props.highImpactChangeCount)}>
                  <Tag
                    text={props.highImpactChangeCount.toString()}
                    color={TagColor.WARN}
                    size={TagSize.SMALL}
                  />
                </Popover>
              </span>
            )}
          </span>
        </div>
        {props.subLabel ? <div className={css(styles.subLabel)}>{props.subLabel}</div> : null}
      </div>
    </>
  );
}

function getParentIconHoverText(highImpactChangeCount: number): string {
  return `${highImpactChangeCount} high impact ${pluralize(highImpactChangeCount, 'change')} to this object`;
}

export function ObjectActionDropdownColumn(props: {
  plan: PrimaryObjectPlan;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  showActionInline?: boolean;
  showIgnore: boolean;
  filteredAdditionalActions?: Array<ObjectAction>;
  disableDropdown?: boolean;
}): JSX.Element {
  return (
    <div className="simpleDivTable-cell">
      <ObjectActionDropdown
        object={props.plan}
        showActionInline={props.showActionInline}
        onUpdateObject={(plan: PrimaryObjectPlan) => {
          props.onUpdatePlan({
            ...plan,
            childMigrationObjectsByType: new Map(),
          });
        }}
        showIgnore={props.showIgnore}
        filteredAdditionalActions={props.filteredAdditionalActions}
        disableDropdown={props.disableDropdown}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  iconCell: {
    marginLeft: -6,
    paddingRight: 18,
  },
  firstCell: {
    marginLeft: 15,
  },
  warningRow: {
    backgroundColor: ORANGE_LIGHTEST,
  },
  errorRow: {
    backgroundColor: RED_POWDER,
  },
  ignoreRow: {
    color: GRAY_DARK,
  },
  parentToggle: {
    cursor: 'pointer',
  },
  toggleWithIcon: {
    marginLeft: 7,
  },
  noToggle: {
    marginLeft: 15,
  },
  childRow: {
    backgroundColor: Colors.GRAY_LIGHTER,
    border: 'none',
  },
  subLabel: {
    display: 'inline-block',
    minWidth: '0',
    marginLeft: 28,
  },
  subLabelHeight: {
    lineHeight: '14px',
  },
  highImpactParentIcon: {
    marginLeft: 6,
  },
  lockIconBlockWrapper: {
    marginRight: 8,
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  lockIconWrapper: {
    marginRight: 8,
    display: 'flex',
    alignItems: 'center',
  },
  lockIconInlineWrapper: {
    marginRight: 6,
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
  },
});
